/**
 * Pending Organization Approvals — surfaces companies that have
 * completed their billing profile and are awaiting an admin to sync
 * them to e-conomic. Approval belongs with the company/billing
 * profile so this panel lives on the Companies list view (2026-02).
 *
 * Extracted from UsersPage.jsx unchanged in behaviour — same API
 * endpoints, same UX.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Building2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
    Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../ui/card';
import { organizationsApi } from '../../lib/api';

export default function PendingOrgApprovalsPanel() {
    const [pendingOrgs, setPendingOrgs] = useState([]);
    const [approvingOrgId, setApprovingOrgId] = useState(null);

    const fetchPending = async () => {
        try {
            const response = await organizationsApi.getPendingApproval();
            setPendingOrgs(response.data);
        } catch (_e) {
            // Silent: this is an admin-only surface and a failed load
            // shouldn't spam the toast queue on every mount.
        }
    };

    useEffect(() => { fetchPending(); }, []);

    const handleApprove = async (orgId) => {
        try {
            setApprovingOrgId(orgId);
            await organizationsApi.approveAndSync(orgId);
            toast.success('Organization approved and synced to e-conomic');
            fetchPending();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to approve organization');
        } finally {
            setApprovingOrgId(null);
        }
    };

    if (pendingOrgs.length === 0) return null;

    return (
        <Card
            className="mb-6 border-blue-200 bg-blue-50/50"
            data-testid="pending-org-approvals-panel"
        >
            <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                    <Building2 className="h-5 w-5" />
                    Pending Organization Approvals
                    <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                        {pendingOrgs.length}
                    </span>
                </CardTitle>
                <CardDescription>
                    Organizations that have completed their billing profile and are waiting for approval to sync with e-conomic.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    {pendingOrgs.map((org) => (
                        <div
                            key={org.id}
                            className="flex items-center justify-between p-4 bg-white rounded-lg border border-blue-100"
                        >
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-slate-900">{org.name}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        org.type === 'business' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'
                                    }`}>
                                        {org.type === 'business' ? 'Business' : 'Private'}
                                    </span>
                                </div>
                                <div className="text-sm text-slate-500 mt-1">
                                    {org.country && <span className="mr-3">Country: {org.country}</span>}
                                    {org.currency && <span className="mr-3">Currency: {org.currency}</span>}
                                    {org.vat_id && <span>VAT: {org.vat_id}</span>}
                                </div>
                                {org.billing_address && (
                                    <div className="text-xs text-slate-400 mt-1">
                                        {org.billing_address.address}, {org.billing_address.postal_code} {org.billing_address.city}
                                    </div>
                                )}
                            </div>
                            <Button
                                onClick={() => handleApprove(org.id)}
                                disabled={approvingOrgId === org.id}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid={`approve-org-${org.id}`}
                            >
                                {approvingOrgId === org.id ? (
                                    <span className="flex items-center gap-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Syncing...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4" />
                                        Approve &amp; Sync
                                    </span>
                                )}
                            </Button>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}
